/**
 * External tasks v1 (260702) bod 2 — External-only assignee picker for the
 * external-audience TaskDetailSlideOver.
 *
 * Deliberately SEPARATE from AssigneePicker: the internal picker mounts
 * `useProjectAssignments` / `useReassignTask`, both of which route through the
 * internal client.ts (clearing the internal auth token on 401 — Phase 23
 * Landmine #8). The external surface must never reach into internal client
 * state, so this picker uses the isolated external board API client + the
 * external reassign endpoint (cookie creds).
 *
 * MKS-132 — rendered for ANY external member of the project (the backend
 * enforces membership; any 4xx surfaces as a toast). Mirrors AssigneePicker's
 * Internal/External tab bar (Phase 66 EAP-06): the Internal tab lists
 * internal project members (names only — the roster endpoint never exposes
 * internal emails to externals), the External tab lists external members.
 * The current assignee is excluded from the target list.
 */
import { useCallback, useRef, useState, type ComponentRef } from 'react';
import { Avatar } from '../ui/Avatar';
import { useProjectExternalMembersExternal } from '../../hooks/useProjectExternalMembersExternal';
import { useExternalReassign } from '../../hooks/useExternalReassign';
import { useToast } from '../ui/Toast';

export interface ExternalAssigneePickerProps {
  taskId: number;
  projectId: number;
  /** The task's current assignee (either kind) — excluded from the target list. */
  currentAssigneeId: number | null;
  currentAssigneeKind: 'user' | 'external_user' | null;
  currentAssigneeName: string | null;
}

export function ExternalAssigneePicker({
  taskId,
  projectId,
  currentAssigneeId,
  currentAssigneeKind,
  currentAssigneeName,
}: ExternalAssigneePickerProps) {
  const {
    data: members = [],
    isLoading,
    isError,
    refetch,
  } = useProjectExternalMembersExternal(projectId);
  const reassign = useExternalReassign();
  const showToast = useToast();
  const detailsRef = useRef<ComponentRef<'details'> | null>(null);

  // Same default as AssigneePicker: open on the tab holding the current
  // assignee so they are visible on first open.
  const [activeTab, setActiveTab] = useState<'internal' | 'external'>(
    currentAssigneeKind === 'external_user' ? 'external' : 'internal',
  );

  const targets = members
    .filter((m) => !(m.kind === currentAssigneeKind && m.id === currentAssigneeId))
    .map((m) => ({
      kind: m.kind,
      id: m.id,
      // Project convention: "Lastname Firstname" in pickers.
      name: `${m.lastName ?? ''} ${m.firstName ?? ''}`.trim() || m.email || `#${m.id}`,
    }))
    .sort((x, y) => x.name.localeCompare(y.name));
  const tabTargets = targets.filter((m) =>
    activeTab === 'internal' ? m.kind === 'user' : m.kind === 'external_user',
  );

  const close = useCallback(() => {
    if (detailsRef.current) detailsRef.current.open = false;
  }, []);

  const handlePick = useCallback(
    (target: { kind: 'user' | 'external_user'; id: number }) => {
      reassign.mutate(
        { taskId, target, projectId },
        {
          onError: (err) => {
            const msg = err instanceof Error ? err.message : 'Reassignment failed';
            showToast(msg, 'error');
          },
          onSuccess: () => close(),
        },
      );
    },
    [reassign, taskId, projectId, showToast, close],
  );

  const chipLabel = currentAssigneeName ?? 'Unassigned';

  return (
    <details
      ref={detailsRef}
      className="relative"
      data-testid="task-detail-external-assignee-picker"
    >
      <summary
        className={`list-none cursor-pointer flex items-center gap-1.5 px-2 py-1 bg-ms-canvas border border-ms-border-subtle rounded-full hover:bg-ms-surface-2 ${
          reassign.isPending ? 'opacity-60 pointer-events-none' : ''
        }`}
        aria-label="Reassign to another project member"
      >
        <Avatar name={chipLabel} size="xs" />
        <span className="text-[11px] font-medium text-ms-text-secondary">{chipLabel}</span>
        {currentAssigneeKind === 'external_user' && (
          <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
            External
          </span>
        )}
        <svg
          aria-hidden="true"
          className="h-3 w-3 opacity-70"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </summary>

      <div
        className="absolute right-0 top-full mt-1 z-20 min-w-[240px] max-h-72 overflow-y-auto bg-ms-surface border border-ms-border rounded-lg shadow-lg py-1"
        role="menu"
      >
        {/* MKS-132 — same tab bar as AssigneePicker (Phase 66 EAP-06). */}
        <div
          className="flex border-b border-ms-border-subtle"
          role="tablist"
          aria-label="Assignee type"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'internal'}
            onClick={() => setActiveTab('internal')}
            className={`flex-1 px-3 py-1.5 text-[11px] font-medium ${
              activeTab === 'internal'
                ? 'text-primary-600 border-b-2 border-primary-500'
                : 'text-ms-text-muted hover:text-ms-text-secondary'
            }`}
          >
            Internal
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'external'}
            onClick={() => setActiveTab('external')}
            className={`flex-1 px-3 py-1.5 text-[11px] font-medium ${
              activeTab === 'external'
                ? 'text-primary-600 border-b-2 border-primary-500'
                : 'text-ms-text-muted hover:text-ms-text-secondary'
            }`}
          >
            External
          </button>
        </div>

        {isLoading ? (
          <div className="px-3 py-2 text-[11px] text-ms-text-muted">Loading members…</div>
        ) : isError ? (
          <div className="px-3 py-2 text-[11px] text-ms-text-muted">
            Couldn’t load members.
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-1.5 text-primary-600 hover:underline"
            >
              Retry
            </button>
          </div>
        ) : tabTargets.length === 0 ? (
          <div className="px-3 py-2 text-[11px] text-ms-text-muted">
            {activeTab === 'internal'
              ? 'No internal members to assign.'
              : 'No other external members to assign.'}
          </div>
        ) : (
          tabTargets.map((m) => (
            <button
              key={`${m.kind}-${m.id}`}
              type="button"
              role="menuitem"
              onClick={() => handlePick({ kind: m.kind, id: m.id })}
              disabled={reassign.isPending}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-ms-text-secondary hover:bg-ms-canvas disabled:cursor-not-allowed"
            >
              <Avatar name={m.name} size="xs" />
              <span className="flex-1 truncate">{m.name}</span>
              {m.kind === 'external_user' && (
                <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                  External
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </details>
  );
}
